import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from 'react-apollo';
import { Modal, Toast, ToastTitle } from '@sm/wds-react';
import { t } from '@sm/intl';

import RequestModalContent from './RequestModalContent';
import PendingModalContent from './PendingModalContent';
import NotAvailableModalContent from './NotAvailableModalContent';
import SeatRequestQuery from './graphql/SeatRequest.graphql';

import COPY from './SeatRequestCopy';
import { STATUS } from './constants';

function SeatRequestModal({
  show,
  onClose,
  seatRequestData,
  sourceCategoryKey,
  sourceFeatureKey,
  showToasts,
}) {
  const [toastMessage, setToastMesssage] = useState(null);
  const [operationStatus, setOperationStatus] = useState(null);

  // we intentionally separate the prop show, from where
  const [showModal, setShowModal] = useState(show);

  useEffect(() => {
    setShowModal(show);
  }, [show]);

  // called when user has completed interaction and
  // we either want to show a toast or immediately
  // signal to the parent that we are done.
  const onModalFinished = result => {
    setShowModal(false);
    if (showToasts && result?.message && typeof result.message === 'string') {
      setToastMesssage(result.message);
      setOperationStatus(result.status);
    } else if (
      result?.status === STATUS.SUCCESS ||
      result?.status === STATUS.FAILURE
    ) {
      onClose(result);
    } else {
      onClose({ status: STATUS.SUCCESS, message: null });
    }
  };

  // called after toasts have finished displaying
  const onOperationFinished = () => {
    setToastMesssage(null);
    setShowModal(false);
    onClose({ status: operationStatus, message: toastMessage });
  };

  const { loading: isLoadingSeatRequest, data } = useQuery(SeatRequestQuery, {
    skip: seatRequestData || !show,
    fetchPolicy: 'network-only',
    onError: () => {
      onModalFinished({ status: STATUS.FAILURE, message: t(COPY.TOAST_ERROR) });
    },
  });

  const seatRequest = seatRequestData?.seatRequest || data?.user.seatRequest;
  const team = seatRequestData?.team || data?.user.team;

  if (isLoadingSeatRequest || !seatRequest) {
    return null;
  }

  return (
    <>
      <Modal size="md" show={showModal} onClose={onModalFinished}>
        <Choose>
          <When
            condition={
              seatRequest.status === 'AVAILABLE' ||
              seatRequest.status === 'RESPONDED'
            }
          >
            <RequestModalContent
              onModalFinished={onModalFinished}
              sourceCategoryKey={sourceCategoryKey}
              sourceFeatureKey={sourceFeatureKey}
            />
          </When>
          <When condition={seatRequest.status === 'PENDING'}>
            <PendingModalContent
              onModalFinished={onModalFinished}
              seatRequest={seatRequest}
              team={team}
            />
          </When>
          <When condition={seatRequest.status === 'NOT_AVAILABLE'}>
            <NotAvailableModalContent
              onModalFinished={onModalFinished}
              team={team}
            />
          </When>
        </Choose>
      </Modal>
      <Toast show={toastMessage} onClose={onOperationFinished} dismissible>
        <ToastTitle>{toastMessage}</ToastTitle>
      </Toast>
    </>
  );
}

SeatRequestModal.propTypes = {
  /** set to true when you want to show the seat request modal */
  show: PropTypes.bool.isRequired,
  /** Modal will inform you that the operation is completed */
  onClose: PropTypes.func.isRequired,
  /** Optionally pass in the seat request data yourself if you have already queried it.
   *  If this is null, the modal will make it's own request for this data.
   */
  seatRequestData: PropTypes.shape({
    team: PropTypes.shape({
      accountOwner: PropTypes.shape({
        email: PropTypes.string.isRequired,
        firstName: PropTypes.string,
        lastName: PropTypes.string,
      }).isRequired,
    }).isRequired,
    seatRequest: PropTypes.shape({
      status: PropTypes.string.isRequired,
    }),
  }),
  /** This is a key that that maps to a category of features that the user is requesting a seat upgrade for
   *  This could be
   */
  sourceCategoryKey: PropTypes.string.isRequired,
  /** This represents the specific feature the user is requesting a seat upgrade for.
   *  It should match a feature key in the pricing service.
   */
  sourceFeatureKey: PropTypes.string,
  /**
   * Show toasts
   */
  showToasts: PropTypes.bool,
};

SeatRequestModal.defaultProps = {
  seatRequestData: null,
  sourceFeatureKey: null,
  showToasts: true,
};

export default SeatRequestModal;
